import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectApexAccounts } from '@/store/features/apex/apex-selectors';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { fetchPlaidWealthSnapshot } from '@/store/features/plaid/plaid-thunks';
import { selectUser } from '@/store/features/userinfo/userinfoSlice';
import { fetchBankLinkOptions } from '@/api/custom';
import { formatAccountType } from '@/util/account-type-format';
import {
  BankLinkOptionsResponse,
  BankLinkWallet,
  FundingChip,
  bankLinkPickerPath,
  fundingBankInitials,
  fundingBankLogoSrc,
  fundingChipForWallet,
} from '@/util/bank-link';
import {
  getAccountFundingPlaidId,
  getDepositEligiblePlaidAccounts,
} from './deposit-account-selection';
import FundingBankReplacePanel from './FundingBankReplacePanel';
import PlaidLinkModal from './PlaidLinkModal';
import { hasPlaidRedirectQueryParams } from './plaid-redirect-utils';
import './bank-link-picker.scss';

const walletTitle = (wallet: BankLinkWallet): string =>
  formatAccountType(wallet.goal) || 'Brokerage account';

// account_name is Apex's resource path ("accounts/01M…"), so the number
// comes from the Apex account list when it has loaded.
const accountNumberTail = (name?: string): string => {
  const digits = name?.replace(/\D/g, '') ?? '';
  return digits.length >= 4 ? `••${digits.slice(-4)}` : '';
};

const chipBankLabel = (chip: FundingChip): string =>
  [chip.bankName || 'Linked bank', chip.accountType, chip.last4 ? `••${chip.last4}` : '']
    .filter(Boolean)
    .join(' ');

const STATUS_LABELS: Record<FundingChip['status'], string> = {
  connected: 'Active',
  pending: 'Pending with Apex',
  not_set: 'Not set',
  disconnected: 'Disconnected',
};

/**
 * Funding banks (board BankLink): every brokerage account with the bank it
 * deposits from and withdraws to. Change opens the same replace panel the
 * account page uses, inline under the row.
 */
const BankLinkPicker: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const requestedAccountId = searchParams.get('accountId') ?? '';
  const plaidAccounts = useAppSelector(selectPlaidAccounts);
  const apexAccounts = useAppSelector(selectApexAccounts);
  const user = useAppSelector(selectUser);
  const activeUserId = user?.userId || user?.cognito_id || null;
  const depositPlaidAccounts = useMemo(
    () => getDepositEligiblePlaidAccounts(plaidAccounts),
    [plaidAccounts],
  );

  const [options, setOptions] = useState<BankLinkOptionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plaidSnapshotLoading, setPlaidSnapshotLoading] = useState(false);
  const [plaidSnapshotFailed, setPlaidSnapshotFailed] = useState(false);
  const [changingId, setChangingId] = useState<string | null>(null);
  const [plaidModalVisible, setPlaidModalVisible] = useState(() =>
    hasPlaidRedirectQueryParams(searchParams),
  );
  const openedRequestedRef = useRef(false);
  const plaidSnapshotRequestedForUserRef = useRef<string | null>(null);
  const plaidSnapshotRequestRef = useRef<{
    abort: () => void;
    unwrap: () => Promise<unknown>;
  } | null>(null);
  const plaidReturnPath = bankLinkPickerPath(requestedAccountId);

  const eligiblePlaidAccounts = useMemo(() => {
    const fundingAccountIds = options?.funding_account_ids;
    if (fundingAccountIds === undefined) {
      return depositPlaidAccounts;
    }
    const allowedFundingIds = new Set(fundingAccountIds);
    return depositPlaidAccounts.filter((account) =>
      allowedFundingIds.has(getAccountFundingPlaidId(account)),
    );
  }, [depositPlaidAccounts, options?.funding_account_ids]);
  const hasEligiblePlaidAccounts = eligiblePlaidAccounts.length > 0;

  const loadOptions = useCallback(async () => {
    setError(null);
    try {
      setOptions(await fetchBankLinkOptions());
    } catch (loadError) {
      console.error('Failed to load bank link options', loadError);
      setOptions(null);
      setError('We could not load your accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const requestPlaidSnapshot = useCallback(
    (userId: string) => {
      const previousRequest = plaidSnapshotRequestRef.current;
      plaidSnapshotRequestRef.current = null;
      previousRequest?.abort();

      plaidSnapshotRequestedForUserRef.current = userId;
      setPlaidSnapshotFailed(false);
      setPlaidSnapshotLoading(true);

      const request = dispatch(fetchPlaidWealthSnapshot({ range: 'ALL' }));
      plaidSnapshotRequestRef.current = request;
      void request
        .unwrap()
        .catch(() => {
          if (plaidSnapshotRequestRef.current === request) {
            plaidSnapshotRequestedForUserRef.current = null;
            setPlaidSnapshotFailed(true);
          }
        })
        .finally(() => {
          if (plaidSnapshotRequestRef.current === request) {
            setPlaidSnapshotLoading(false);
          }
        });
    },
    [dispatch],
  );

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    if (
      !activeUserId ||
      hasEligiblePlaidAccounts ||
      plaidSnapshotRequestedForUserRef.current === activeUserId
    ) {
      return;
    }

    requestPlaidSnapshot(activeUserId);
  }, [activeUserId, hasEligiblePlaidAccounts, requestPlaidSnapshot]);

  useEffect(
    () => () => {
      const request = plaidSnapshotRequestRef.current;
      plaidSnapshotRequestRef.current = null;
      request?.abort();
    },
    [],
  );

  const wallets = useMemo(() => options?.wallets ?? [], [options]);

  // Opened for one account (?accountId=), or a new account still needs a
  // bank: start with that account's panel open, once.
  useEffect(() => {
    if (openedRequestedRef.current || !hasEligiblePlaidAccounts) {
      return;
    }
    const target =
      wallets.find((wallet) => wallet.account_id === requestedAccountId) ??
      wallets.find((wallet) => wallet.slot_available);
    if (target) {
      openedRequestedRef.current = true;
      setChangingId(target.account_id);
    }
  }, [wallets, requestedAccountId, hasEligiblePlaidAccounts]);

  const renderBank = (wallet: BankLinkWallet) => {
    const chip = fundingChipForWallet(wallet, depositPlaidAccounts);
    if (chip.status === 'not_set') {
      return (
        <span className={`funding-banks-chip is-${chip.status}`}>{STATUS_LABELS.not_set}</span>
      );
    }
    const logo = fundingBankLogoSrc(chip.bankLogo);
    return (
      <>
        {logo ? (
          <img src={logo} alt="" className="funding-banks-mark is-logo" />
        ) : (
          <span className="funding-banks-mark" aria-hidden="true">
            {fundingBankInitials(chip.bankName)}
          </span>
        )}
        <span className="funding-banks-bank-name">{chipBankLabel(chip)}</span>
        <span className={`funding-banks-chip is-${chip.status}`}>{STATUS_LABELS[chip.status]}</span>
      </>
    );
  };

  const renderRow = (wallet: BankLinkWallet) => {
    const changing = changingId === wallet.account_id;
    const needsBank = wallet.slot_available;
    const title = walletTitle(wallet);
    const numberTail = accountNumberTail(
      apexAccounts?.find((account) => account.id === wallet.account_id)?.name,
    );
    const row = (
      <div className="funding-banks-row">
        <span className="funding-banks-account">
          {title}
          {numberTail && <span className="funding-banks-muted"> {numberTail}</span>}
        </span>
        <span className="funding-banks-bank">{renderBank(wallet)}</span>
        <span className="funding-banks-action">
          {hasEligiblePlaidAccounts && !changing && (
            <Button
              type={needsBank ? 'primary' : 'default'}
              aria-label={`${needsBank ? 'Choose bank' : 'Change'} for ${title}`}
              onClick={() => setChangingId(wallet.account_id)}
            >
              {needsBank ? 'Choose bank' : 'Change'}
            </Button>
          )}
        </span>
      </div>
    );

    if (!changing) {
      return <li key={wallet.account_id}>{row}</li>;
    }
    return (
      <li key={wallet.account_id} className="is-changing">
        {row}
        <section className="funding-banks-change" aria-label={`Change funding bank for ${title}`}>
          <FundingBankReplacePanel
            chip={fundingChipForWallet(wallet, depositPlaidAccounts)}
            wallet={wallet}
            pendingSources={options?.pending_sources ?? []}
            allowedFundingIds={options?.funding_account_ids}
            onClose={() => setChangingId(null)}
            onUpdated={() => void loadOptions()}
          />
        </section>
      </li>
    );
  };

  return (
    <div className="funding-banks">
      <nav className="funding-banks-crumbs" aria-label="Breadcrumb">
        <Link to="/home/wealth">Wealth</Link> / Funding banks
      </nav>
      <header className="funding-banks-head">
        <div>
          <h1>Funding banks</h1>
          <p>Each account deposits from and withdraws to one bank.</p>
        </div>
        <Button onClick={() => setPlaidModalVisible(true)}>+ Link another bank</Button>
      </header>

      {loading ? (
        <div className="funding-banks-loading">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
              action={<Button onClick={() => void loadOptions()}>Try again</Button>}
            />
          )}

          {options && wallets.length === 0 && (
            <p className="funding-banks-empty">
              You don&apos;t have a brokerage account yet. Open one from Wealth, then pick its bank
              here.
            </p>
          )}

          {wallets.length > 0 && !hasEligiblePlaidAccounts && (
            <div className="funding-banks-no-bank">
              <strong>Link a checking or savings account first</strong>
              <p>Deposits and withdrawals move money between your accounts and a bank you link.</p>
              <div className="funding-banks-no-bank-actions">
                {plaidSnapshotFailed && (
                  <Button
                    onClick={() => activeUserId && requestPlaidSnapshot(activeUserId)}
                    loading={plaidSnapshotLoading}
                    disabled={!activeUserId || plaidSnapshotLoading}
                  >
                    Try again
                  </Button>
                )}
                <Button type="primary" onClick={() => setPlaidModalVisible(true)}>
                  Link an account
                </Button>
              </div>
            </div>
          )}

          {wallets.length > 0 && (
            <section className="funding-banks-card" aria-label="Accounts">
              <div className="funding-banks-scroll">
                <div className="funding-banks-row is-head" aria-hidden="true">
                  <span>Account</span>
                  <span>Funding bank</span>
                  <span />
                </div>
                <ul>{wallets.map(renderRow)}</ul>
              </div>
            </section>
          )}
        </>
      )}

      <PlaidLinkModal
        visible={plaidModalVisible}
        returnPath={plaidReturnPath}
        onClose={() => setPlaidModalVisible(false)}
        onSuccess={() => {
          setPlaidModalVisible(false);
          void loadOptions();
        }}
      />
    </div>
  );
};

export default BankLinkPicker;
